import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';

import { usePath, usePermissions } from '~/hooks';
import { useLocationsQuery } from '~/hooks/locations';
import layout from '~/hooks/layout';
import { DisplayModeButton, PageList } from '~/layout';
import LocationCreate from './components/LocationCreate';
import Filters from './components/Filters';
import LocationShow from './components/LocationShow';
import LocationsTable from './components/LocationsTable';
import { NoPermission } from '~/components';
import Map from './components/Map';
import { GOOGLE_MAP_API_KEY } from '~/env';
import CardsList from './components/CardsList';

const LocationsPage: FC<RouteComponentProps> = () => {
  const { t } = useTranslation('locations');
  const { data, isLoading, isFetching, total } = useLocationsQuery();
  const { changePath } = usePath();
  const isMobile = layout.useIsMobile();
  const [mode] = DisplayModeButton.useDisplayMode(isMobile ? 'cards' : 'table');

  const hasAccess = usePermissions('business.location.list');
  const hasAccessToCreate = usePermissions('business.location.create');
  if (!hasAccess) return <NoPermission />;
  return (
    <>
      <LocationShow />
      <PageList
        header={{
          heading: t('locations'),
          searchPlaceholder: t('searchPlaceholder'),
          onSearch: (name: string) => changePath({ filter: { name } }),
          queryParam: 'name',
          isFetching,
        }}
        filters={<Filters />}
        cards={<CardsList data={data} loading={isLoading} total={total} />}
        table={<LocationsTable data={data} loading={isLoading} total={total} />}
        map={<Map />}
        absoluteHeader={mode === 'map'}
        add={hasAccessToCreate ? <LocationCreate /> : null}
        displayModeOptions={[{ mode: 'table' }, { mode: 'map', disabled: !GOOGLE_MAP_API_KEY }]}
      />
    </>
  );
};

export default LocationsPage;
